import { animated, config, useTransition } from '@react-spring/web';
import useComponentSize from '@rehooks/component-size';
import { useRef } from 'react';
import styled from 'styled-components';

import { ContractMessage } from '../../../../types/ContractMessage';
import { SupplierMessage } from '../../../../types/SupplierMessage';
import { Checkbox } from '../../../cdl/Checkbox/Checkbox';
import { InputLabelText } from '../../../cdl/Input/components/InputLabelText';
import { Label } from '../../../cdl/Label/Label';
import { Radio, RadioGroup, RadioGroupItem } from '../../../cdl/RadioGroup/RadioGroup';
import { Tag } from '../../../cdl/Tag/Tag';
import { Text } from '../../../cdl/Text/Text';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { Box } from '../../../common/ui/Box';

const ContractRadiosPanel = styled(animated.div)`
    overflow: hidden;
`;

export interface SupplierEntry {
    supplier: SupplierMessage;
    contracts: ContractMessage[];
    alreadyInvited: boolean;
}

interface RequestSupplierOptionProps {
    entry: SupplierEntry;
    selected: boolean;
    selectedContractId: string | undefined;
    vesselDefaultSupplierId: string | undefined;
    vesselDefaultContractId: string | undefined;
    onToggle: () => void;
    onSelectContract: (contractId: string) => void;
}

export const RequestSupplierOption = ({
    entry,
    selected,
    selectedContractId,
    vesselDefaultSupplierId,
    vesselDefaultContractId,
    onToggle,
    onSelectContract,
}: RequestSupplierOptionProps) => {
    const hasContracts = entry.contracts.length > 0;
    const showContractRadios = entry.contracts.length > 1 && selected;

    const contractSummary =
        entry.contracts.length === 1
            ? entry.contracts[0].name
            : translate('order.detail.requestSuppliers.contractCount', { count: String(entry.contracts.length) });
    const separator = `${Characters.EN_SPACE}${Characters.MIDDLE_DOT}${Characters.EN_SPACE}`;

    const label = hasContracts ? (
        <Box display="flex" alignItems="center" minWidth={0}>
            <Text variant="body">{entry.supplier.name}</Text>
            <Text
                variant="body"
                color="foreground.subtle"
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
                {separator}
                {contractSummary}
            </Text>
        </Box>
    ) : (
        entry.supplier.name
    );

    const contractRadiosBodyRef = useRef<HTMLDivElement>(null);
    const { height } = useComponentSize(contractRadiosBodyRef);
    const contractRadiosTransition = useTransition(showContractRadios, {
        from: {
            opacity: 0,
            height: 0,
        },
        leave: {
            opacity: 0,
            height: 0,
        },
        update: {
            opacity: 1,
            height: height + 1,
        },
        config: {
            ...config.default,
            tension: 300,
            clamp: true,
        },
    });

    return (
        <>
            <Checkbox
                label={label}
                checked={entry.alreadyInvited || selected}
                disabled={entry.alreadyInvited}
                onChange={onToggle}
            />

            {contractRadiosTransition((style, isOpen) => {
                if (!isOpen) {
                    return null;
                }

                return (
                    <ContractRadiosPanel style={style}>
                        <Box ref={contractRadiosBodyRef} paddingLeft={26} paddingBottom={8} display="grid" rowGap={6}>
                            <InputLabelText>{translate('order.detail.requestSuppliers.contractsLabel')}</InputLabelText>
                            <RadioGroup value={selectedContractId ?? ''} onValueChange={onSelectContract}>
                                {entry.contracts.map((contract) => {
                                    const isDefaultContract =
                                        entry.supplier.id === vesselDefaultSupplierId &&
                                        contract.id === vesselDefaultContractId;

                                    return (
                                        <RadioGroupItem key={contract.id}>
                                            <Radio value={contract.id} />
                                            <Label asChild>
                                                <Box as="label" display="flex" alignItems="center" gap={8} minWidth={0}>
                                                    <Text variant="small">{contract.name}</Text>
                                                    {isDefaultContract ? (
                                                        <Tag>{translate('labelList.default')}</Tag>
                                                    ) : null}
                                                </Box>
                                            </Label>
                                        </RadioGroupItem>
                                    );
                                })}
                            </RadioGroup>
                        </Box>
                    </ContractRadiosPanel>
                );
            })}
        </>
    );
};
